import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { Camera, Save, User, ArrowLeft, Sparkles, Check, Loader2, Briefcase, Shirt, Crown, Globe, Rocket, Shield, ImageIcon, X, Building2, ChevronRight } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

// Map icon keys to Lucide components
const ICON_MAP = {
  briefcase: Briefcase,
  shirt: Shirt,
  crown: Crown,
  globe: Globe,
  rocket: Rocket,
  shield: Shield,
  sparkles: Sparkles,
};

const POLL_INTERVAL = 3000; // 3 seconds
const POLL_TIMEOUT = 120000; // 2 minutes

const ProfileEdit = () => {
  const { user, refreshUser } = useAuth();
  const { userTheme } = useTheme();
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  // AI photo enhancement state
  const [photoStyles, setPhotoStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [selectingImage, setSelectingImage] = useState(null);
  const pollingRef = useRef(null);
  const pollingStartRef = useRef(null);

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const cardStyles = userTheme?.cards || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    fetchImageGenStatus();
    fetchPhotoStyles();
  }, []);

  const fetchImageGenStatus = async () => {
    try {
      const res = await api.get('/ai/profile-images/status');
      setGenStatus(res.data);
    } catch (error) {
      // AI features may not be configured - silently ignore
    }
  };

  const fetchPhotoStyles = async () => {
    try {
      const res = await api.get('/ai/photo-style-prompts');
      setPhotoStyles(res.data.prompts || []);
    } catch (error) {
      // Styles not available
    }
  };

  const getProfilePhoto = () => {
    if (previewUrl) return previewUrl;
    if (user?.profile_photo) return resolveFileUrl(user.profile_photo, API_URL);
    return null;
  };

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((generationId) => {
    stopPolling();
    pollingStartRef.current = Date.now();

    pollingRef.current = setInterval(async () => {
      // Timeout check
      if (Date.now() - pollingStartRef.current > POLL_TIMEOUT) {
        stopPolling();
        setGenerating(false);
        toast.error('Photo generation is taking longer than expected. Check back later.');
        return;
      }

      try {
        const res = await api.get(`/ai/profile-images/generation/${generationId}`);
        const { status, result_photo, style_title } = res.data;

        if (status === 'completed') {
          stopPolling();
          setGenerating(false);
          toast.success(`"${style_title}" photo is ready!`);
          // Refresh the status to get updated library
          await fetchImageGenStatus();
        } else if (status === 'failed') {
          stopPolling();
          setGenerating(false);
          setSelectedStyle(null);
          toast.error(res.data.error || 'Photo generation failed. Please try again.');
          await fetchImageGenStatus();
        }
      } catch (error) {
        // Network error during polling - keep trying
      }
    }, POLL_INTERVAL);
  }, [stopPolling]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/users/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile photo updated!');
      setSelectedStyle(null);
      await refreshUser();
    } catch (error) {
      toast.error('Failed to upload photo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateWithStyle = async (style) => {
    // Must have a photo uploaded first
    if (!user?.profile_photo && !previewUrl) {
      toast.error('Please upload a photo first, then select a professional style.');
      return;
    }

    setSelectedStyle(style._id);
    setGenerating(true);
    try {
      const res = await api.post('/ai/generate-profile-photo', { style_id: style._id });
      toast.success(res.data.message || `"${style.title}" style requested!`);
      setGenStatus(prev => prev ? { ...prev, remaining: res.data.remaining } : null);
      // Start polling for completion
      if (res.data.generation_id) {
        startPolling(res.data.generation_id);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error(detail || 'Enhancement limit reached for this month');
      } else if (error.response?.status === 400) {
        toast.error(detail || 'Please upload a photo first');
      } else {
        toast.error(detail || 'Failed to request photo enhancement');
      }
      setSelectedStyle(null);
      setGenerating(false);
    }
  };

  const handleSelectAsProfile = async (imageUrl) => {
    setSelectingImage(imageUrl);
    try {
      await api.post('/ai/select-profile-image', { image_url: imageUrl });
      toast.success('Profile photo updated!');
      setPreviewUrl(null);
      await refreshUser();
      await fetchImageGenStatus();
    } catch (error) {
      toast.error('Failed to update profile photo');
    } finally {
      setSelectingImage(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/users/profile', {
        first_name: firstName,
        last_name: lastName
      });

      toast.success('Profile updated successfully!');
      await refreshUser();
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (iconKey) => {
    const IconComp = ICON_MAP[iconKey] || Sparkles;
    return IconComp;
  };

  return (
    <Sidebar>
      <div
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 hover:opacity-80 transition"
          style={{ color: primaryColor }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div
          className="rounded-xl shadow-sm p-8 max-w-xl flex-1"
          style={{
            backgroundColor: cardStyles.backgroundColor || '#FFFFFF',
            border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`
          }}
        >
          <h1
            className="text-2xl font-semibold mb-6"
            style={{ color: cardStyles.headerColor || '#111827' }}
          >
            Edit Profile
          </h1>

          {/* Profile Photo */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative mb-4">
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden border-4"
                style={{
                  backgroundColor: pageStyles.backgroundColor || '#F3F4F6',
                  borderColor: cardStyles.borderColor || '#E5E7EB'
                }}
              >
                {getProfilePhoto() ? (
                  <img
                    src={getProfilePhoto()}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-16 h-16" style={{ color: cardStyles.textColor || '#9CA3AF' }} />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-10 h-10 text-white rounded-full flex items-center justify-center shadow-lg transition-colors disabled:opacity-50 hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
                title="Upload photo"
              >
                <Camera className="w-5 h-5" />
              </button>
              {/* Hidden file input for gallery */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              {/* Hidden file input for camera capture (mobile) */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="user"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Upload & Camera buttons */}
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-lg border transition hover:bg-gray-50 disabled:opacity-50"
                style={{ borderColor: cardStyles.borderColor || '#E5E7EB', color: cardStyles.textColor || '#6B7280' }}
              >
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </button>
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-lg border transition hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                style={{ borderColor: cardStyles.borderColor || '#E5E7EB', color: cardStyles.textColor || '#6B7280' }}
              >
                <Camera className="w-3.5 h-3.5" />
                Take Photo
              </button>
            </div>
            <p className="text-xs" style={{ color: cardStyles.textColor || '#9CA3AF' }}>
              JPG, PNG, or WebP (max 5MB)
            </p>
          </div>

          {/* AI Professional Photo Enhancement */}
          {genStatus && photoStyles.length > 0 && (
            <div
              className="mb-8 rounded-xl p-5"
              style={{
                backgroundColor: `${primaryColor}08`,
                border: `1px solid ${primaryColor}20`
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: primaryColor }} />
                  <h3 className="font-medium text-sm" style={{ color: cardStyles.headerColor || '#111827' }}>
                    AI Professional Photo
                  </h3>
                </div>
                <span className="text-xs px-2 py-1 rounded-full" style={{
                  backgroundColor: `${primaryColor}15`,
                  color: primaryColor
                }}>
                  {genStatus.remaining}/{genStatus.limit} remaining
                </span>
              </div>

              <p className="text-xs mb-4" style={{ color: cardStyles.textColor || '#6B7280' }}>
                Upload your photo above, then choose a professional style to enhance it with AI.
              </p>

              {/* Style Selection Buttons */}
              <div className="grid grid-cols-2 gap-2.5">
                {photoStyles.map((style) => {
                  const IconComp = getIconComponent(style.icon);
                  const isSelected = selectedStyle === style._id;
                  const isDisabled = generating || genStatus.remaining <= 0;

                  return (
                    <button
                      key={style._id}
                      onClick={() => handleGenerateWithStyle(style)}
                      disabled={isDisabled}
                      className={`relative flex items-center gap-2.5 px-3.5 py-3 rounded-xl border-2 text-left transition-all disabled:opacity-50 hover:shadow-md ${
                        isSelected
                          ? 'shadow-md'
                          : 'hover:border-opacity-60'
                      }`}
                      style={{
                        borderColor: isSelected ? primaryColor : (cardStyles.borderColor || '#E5E7EB'),
                        backgroundColor: isSelected ? `${primaryColor}10` : 'white',
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: isSelected ? primaryColor : `${primaryColor}12`,
                        }}
                      >
                        {generating && isSelected ? (
                          <Loader2 className="w-4.5 h-4.5 animate-spin" style={{ color: isSelected ? 'white' : primaryColor }} />
                        ) : (
                          <IconComp className="w-4.5 h-4.5" style={{ color: isSelected ? 'white' : primaryColor }} />
                        )}
                      </div>
                      <span
                        className="text-sm font-medium leading-tight"
                        style={{ color: cardStyles.headerColor || '#111827' }}
                      >
                        {style.title}
                      </span>
                      {isSelected && !generating && (
                        <div
                          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {genStatus.remaining <= 0 && (
                <p className="text-xs text-center mt-3" style={{ color: '#EF4444' }}>
                  Monthly limit reached. You can still upload a photo manually.
                </p>
              )}

              {/* Photo Library */}
              {(() => {
                const savedImages = (genStatus.saved_images || []).filter(
                  img => img.status === 'completed' || img.status === 'processing' || img.status === 'pending'
                );
                if (savedImages.length === 0 && !generating) return null;

                return (
                  <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${primaryColor}15` }}>
                    <div className="flex items-center gap-2 mb-3">
                      <ImageIcon className="w-4 h-4" style={{ color: primaryColor }} />
                      <h4 className="text-xs font-medium" style={{ color: cardStyles.headerColor || '#111827' }}>
                        Your Generated Photos
                      </h4>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {savedImages.map((img, idx) => {
                        const isActive = user?.profile_photo && img.result_photo && user.profile_photo === img.result_photo;
                        const isLoading = img.status === 'pending' || img.status === 'processing';
                        const isSelecting = selectingImage === img.result_photo;

                        return (
                          <div key={img.generation_id || idx} className="relative group">
                            <div
                              className="aspect-square rounded-lg overflow-hidden border-2 transition-all"
                              style={{
                                borderColor: isActive ? primaryColor : (cardStyles.borderColor || '#E5E7EB'),
                                backgroundColor: pageStyles.backgroundColor || '#F3F4F6',
                              }}
                            >
                              {isLoading ? (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
                                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
                                  <span className="text-[10px]" style={{ color: cardStyles.textColor || '#9CA3AF' }}>
                                    Generating...
                                  </span>
                                </div>
                              ) : img.result_photo ? (
                                <img
                                  src={resolveFileUrl(img.result_photo, API_URL)}
                                  alt={img.style_title || 'Generated photo'}
                                  className="w-full h-full object-cover"
                                />
                              ) : null}
                            </div>
                            <p className="text-[10px] mt-1 text-center truncate" style={{ color: cardStyles.textColor || '#6B7280' }}>
                              {img.style_title || 'AI Photo'}
                            </p>
                            {/* Use as Profile button */}
                            {img.status === 'completed' && img.result_photo && !isActive && (
                              <button
                                onClick={() => handleSelectAsProfile(img.result_photo)}
                                disabled={isSelecting}
                                className="mt-1 w-full text-[10px] py-1 rounded-md text-white transition hover:opacity-90 disabled:opacity-50"
                                style={{ backgroundColor: primaryColor }}
                              >
                                {isSelecting ? 'Setting...' : 'Use as Profile'}
                              </button>
                            )}
                            {isActive && (
                              <div className="mt-1 w-full text-[10px] py-1 rounded-md text-center font-medium" style={{ color: primaryColor }}>
                                Current Photo
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Name Form */}
          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: cardStyles.textColor || '#374151' }}
              >
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:outline-none"
                style={{
                  border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`,
                  color: cardStyles.headerColor || '#111827'
                }}
                placeholder="Enter your first name"
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: cardStyles.textColor || '#374151' }}
              >
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:outline-none"
                style={{
                  border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`,
                  color: cardStyles.headerColor || '#111827'
                }}
                placeholder="Enter your last name"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Edit Business Card */}
        <div
          className="rounded-xl shadow-sm p-8 w-full lg:w-80 lg:sticky lg:top-8"
          style={{
            backgroundColor: cardStyles.backgroundColor || '#FFFFFF',
            border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}12` }}
            >
              <Building2 className="w-6 h-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: cardStyles.headerColor || '#111827' }}
              >
                Business Profile
              </h2>
              <p className="text-xs" style={{ color: cardStyles.textColor || '#6B7280' }}>
                Company info, photos & docs
              </p>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: cardStyles.textColor || '#6B7280' }}>
            Manage your company's profile information, upload photos and documents that will appear in your business directory listing.
          </p>
          <button
            onClick={() => navigate('/admin/company-settings?tab=media')}
            className="w-full text-white py-2.5 px-4 rounded-lg font-medium flex items-center justify-center gap-2 hover:opacity-90 transition"
            style={{ backgroundColor: primaryColor }}
          >
            <Building2 className="w-4 h-4" />
            Edit Business Profile
            <ChevronRight className="w-4 h-4 ml-auto" />
          </button>
        </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default ProfileEdit;
